import type {
  MarketShippingPreservationOption,
  MarketShippingRate,
} from "@intelligems/ig-types/dtos/market-shipping-configuration";
import type { MarketShippingSelection } from "@intelligems/ig-types/dtos/shopify-shipping";

type Money = MarketShippingRate["price"];

type Rate =
  MarketShippingPreservationOption["rateGroups"][number]["rates"][number];

export type PreservedFreeShippingRate = {
  price: Money;
  minValue: Money;
  maxValue: Money | null;
  transitTimeMinSeconds: number | null;
  transitTimeMaxSeconds: number | null;
};

export type MarketShippingPreservationAnalysis =
  | { eligible: false; reason: string }
  | {
      eligible: true;
      selections: { selection: MarketShippingSelection; key: string }[];
      freeRatesByGroup: {
        groupId: string;
        rates: PreservedFreeShippingRate[];
      }[];
    };

export function marketShippingSelectionKey(
  selection: MarketShippingSelection
): string {
  return JSON.stringify([
    selection.marketId,
    selection.optionId,
    selection.optionType,
    selection.kind,
    selection.rateGroupId ?? null,
    selection.rateId ?? null,
  ]);
}

/**
 * Keys of the selections whose planned operations realize `selection`: itself,
 * its rate group (removing a whole group removes every rate in it) and the
 * whole option (deactivating an option suppresses everything under it). This
 * is the same subsumption `normalizeMarketSelections` applies when it drops a
 * selection another one covers, so a plan can prove every selection it holds
 * is carried out even when the analysis collapsed several into one.
 */
export function coveringMarketSelectionKeys(
  selection: MarketShippingSelection
): string[] {
  const { marketId, optionId, optionType } = selection;
  const keys = [marketShippingSelectionKey(selection)];
  if (selection.kind === "rate") {
    keys.push(
      marketShippingSelectionKey({
        marketId,
        optionId,
        optionType,
        kind: "rate_group",
        rateGroupId: selection.rateGroupId,
      })
    );
  }
  if (selection.kind !== "option") {
    keys.push(
      marketShippingSelectionKey({
        marketId,
        optionId,
        optionType,
        kind: "option",
      })
    );
  }
  return keys;
}

export function normalizeMarketSelections(
  selections: MarketShippingSelection[]
): MarketShippingSelection[] {
  const unique = [
    ...new Map(
      selections.map((selection) => [
        marketShippingSelectionKey(selection),
        selection,
      ])
    ).values(),
  ];
  return unique
    .filter(
      (selection) =>
        !unique.some((other) => {
          if (
            other === selection ||
            other.marketId !== selection.marketId ||
            other.optionId !== selection.optionId
          ) {
            return false;
          }
          return (
            other.kind === "option" ||
            (other.kind === "rate_group" &&
              selection.kind === "rate" &&
              other.rateGroupId === selection.rateGroupId)
          );
        })
    )
    .sort(
      (a, b) =>
        Number(b.kind === "free_shipping_threshold") -
          Number(a.kind === "free_shipping_threshold") ||
        marketShippingSelectionKey(a).localeCompare(
          marketShippingSelectionKey(b)
        )
    );
}

function compareMoney(left: Money, right: Money): number {
  if (
    left.currencyCode !== right.currencyCode ||
    !/^\d+(?:\.\d+)?$/.test(left.amount) ||
    !/^\d+(?:\.\d+)?$/.test(right.amount)
  ) {
    throw new Error(
      "This shipping option's price limits use mixed currencies or unreadable amounts, so Intelligems cannot keep its free-shipping rule. Fix the price limits in Shopify or choose different rates."
    );
  }
  const [li, lf = ""] = left.amount.split(".");
  const [ri, rf = ""] = right.amount.split(".");
  const precision = Math.max(lf.length, rf.length);
  const a = BigInt(li! + lf.padEnd(precision, "0"));
  const b = BigInt(ri! + rf.padEnd(precision, "0"));
  return a < b ? -1 : a > b ? 1 : 0;
}

function freeRates(
  option: MarketShippingPreservationOption,
  rates: Rate[]
): PreservedFreeShippingRate[] {
  const threshold = option.freeDeliveryMinimumValue;
  if (!threshold) {
    return [];
  }
  compareMoney(threshold, { amount: "0", currencyCode: option.currency });
  const freeRate = (
    rate: Rate,
    minimum: Money,
    maximum: Money | null
  ): PreservedFreeShippingRate => ({
    price: { amount: "0", currencyCode: option.currency },
    minValue: minimum,
    maxValue: maximum,
    transitTimeMinSeconds: rate.transitTimeMinSeconds,
    transitTimeMaxSeconds: rate.transitTimeMaxSeconds,
  });
  if (option.optionType === "value_based") {
    return rates.flatMap((rate) => {
      const minimum = rate.minValue;
      const maximum = rate.maxValue;
      if (!minimum) {
        throw new Error(
          "A price-based rate in this shipping option has no minimum order price, so Intelligems cannot keep its free-shipping rule. Set a minimum in Shopify or choose different rates."
        );
      }
      const belowThreshold = compareMoney(minimum, threshold) < 0;
      if (maximum && compareMoney(maximum, minimum) < 0) {
        throw new Error(
          "A price-based rate in this shipping option has a maximum order price below its minimum. Fix the rate in Shopify or choose different rates."
        );
      }
      if (maximum && compareMoney(maximum, threshold) < 0) {
        return [];
      }
      return [
        freeRate(rate, belowThreshold ? threshold : minimum, maximum ?? null),
      ];
    });
  }
  if (!rates.length) {
    throw new Error(
      "This shipping option has a free-shipping threshold but no rates, so Intelligems cannot keep its free-shipping rule. Add a rate in Shopify or choose different rates."
    );
  }
  if (
    rates.some(
      (rate) =>
        rate.transitTimeMinSeconds !== rates[0]!.transitTimeMinSeconds ||
        rate.transitTimeMaxSeconds !== rates[0]!.transitTimeMaxSeconds
    )
  ) {
    throw new Error(
      "This shipping option's weight tiers have different delivery times, so Intelligems cannot keep its free-shipping rule as one rate. Choose individual rates instead."
    );
  }
  if (option.optionType === "weight_based") {
    const unit = rates[0]!.minWeight?.unit;
    const bounds = rates
      .map((rate) => {
        const min = rate.minWeight;
        const max = rate.maxWeight;
        if (
          !min ||
          !Number.isFinite(min.value) ||
          min.value < 0 ||
          !["GRAMS", "KILOGRAMS", "OUNCES", "POUNDS"].includes(unit ?? "") ||
          min.unit !== unit ||
          (max &&
            (max.unit !== unit ||
              !Number.isFinite(max.value) ||
              max.value < min.value))
        ) {
          throw new Error(
            "This shipping option's weight tiers use mixed units or invalid weights, so Intelligems cannot keep its free-shipping rule. Fix the weight tiers in Shopify or choose individual rates."
          );
        }
        return { min: min.value, max: max?.value ?? Infinity };
      })
      .sort((a, b) => a.min - b.min);
    let covered = 0;
    for (const bound of bounds) {
      if (bound.min > covered) {
        throw new Error(
          "This shipping option's weight tiers leave gaps or stop at a maximum weight, so Intelligems cannot keep its free-shipping rule for every order. Cover all weights in Shopify or choose individual rates."
        );
      }
      covered = Math.max(covered, bound.max);
    }
    if (covered !== Infinity) {
      throw new Error(
        "This shipping option's weight tiers leave gaps or stop at a maximum weight, so Intelligems cannot keep its free-shipping rule for every order. Cover all weights in Shopify or choose individual rates."
      );
    }
  } else if (option.optionType !== "flat_rate") {
    throw new Error(
      "Intelligems can keep a free-shipping threshold only on flat, price-based or weight-based shipping options. Choose individual rates instead."
    );
  }
  return [freeRate(rates[0]!, threshold, null)];
}

/** Supply the complete selection set for one original option in one market. */
export function analyzeMarketShippingPreservation(
  selections: MarketShippingSelection[],
  original: MarketShippingPreservationOption
): MarketShippingPreservationAnalysis {
  try {
    const normalized = normalizeMarketSelections(selections);
    for (const selection of normalized) {
      if (
        selection.optionId !== original.id ||
        selection.optionType !== original.optionType ||
        !original.isActive ||
        selection.marketId !== normalized[0]!.marketId
      ) {
        throw new Error(
          "This shipping option was turned off or changed in Shopify after it was selected. Re-select the shipping rates to replace."
        );
      }
      if (
        selection.kind === "free_shipping_threshold" &&
        !original.freeDeliveryMinimumValue
      ) {
        throw new Error(
          "The free-shipping threshold on this shipping option no longer exists in Shopify. Re-select the shipping rates to replace."
        );
      }
      if (selection.kind === "rate" || selection.kind === "rate_group") {
        const group = original.rateGroups.find(
          (group) => group.id === selection.rateGroupId
        );
        if (!group) {
          throw new Error(
            "A selected group of rates in this shipping option no longer exists in Shopify. Re-select the shipping rates to replace."
          );
        }
        if (
          selection.kind === "rate" &&
          !group.rates.some((rate) => rate.id === selection.rateId)
        ) {
          throw new Error(
            "A selected rate in this shipping option no longer exists in Shopify. Re-select the shipping rates to replace."
          );
        }
      }
    }
    const canonical = normalized.flatMap<{
      selection: MarketShippingSelection;
      key: string;
    }>((selection) => {
      const key = marketShippingSelectionKey(selection);
      if (selection.kind !== "rate") {
        return [{ selection, key }];
      }
      const group = original.rateGroups.find(
        (group) => group.id === selection.rateGroupId
      )!;
      const selected = normalized.filter(
        (candidate) =>
          candidate.kind === "rate" && candidate.rateGroupId === group.id
      );
      if (
        !group.rates.length ||
        !group.rates.every((rate) =>
          selected.some((candidate) => candidate.rateId === rate.id)
        )
      ) {
        return [{ selection, key }];
      }
      if (selection !== selected[0]) {
        return [];
      }
      // Every rate of the group is selected, so one whole-group removal
      // realizes all of them. It carries the group's own key: each rate
      // selection maps back to it through `coveringMarketSelectionKeys`.
      const wholeGroup: MarketShippingSelection = {
        marketId: selection.marketId,
        optionId: selection.optionId,
        optionType: selection.optionType,
        kind: "rate_group",
        rateGroupId: selection.rateGroupId,
      };
      return [
        { selection: wholeGroup, key: marketShippingSelectionKey(wholeGroup) },
      ];
    });
    canonical.sort(
      (a, b) =>
        (a.selection.rateGroupId ?? "").localeCompare(
          b.selection.rateGroupId ?? ""
        ) || a.key.localeCompare(b.key)
    );
    const freeRatesByGroup: Extract<
      MarketShippingPreservationAnalysis,
      { eligible: true }
    >["freeRatesByGroup"] = [];
    if (
      !normalized.some(
        (selection) =>
          selection.kind === "option" ||
          selection.kind === "free_shipping_threshold"
      )
    ) {
      for (const group of original.rateGroups) {
        const selected = canonical.filter(
          ({ selection }) => selection.rateGroupId === group.id
        );
        if (!selected.length) {
          continue;
        }
        const rates = selected.some(
          ({ selection }) => selection.kind === "rate_group"
        )
          ? group.rates
          : group.rates.filter((rate) =>
              selected.some(({ selection }) => selection.rateId === rate.id)
            );
        freeRatesByGroup.push({
          groupId: group.id,
          rates: freeRates(original, rates),
        });
      }
    }
    return { eligible: true, selections: canonical, freeRatesByGroup };
  } catch (error) {
    return {
      eligible: false,
      reason: `${error instanceof Error ? error.message : String(error)}. Change this selection, or select the free-shipping threshold too if you want to replace it.`,
    };
  }
}
